import { EncryptionService } from "@marketplace/shared/services";
import { captureException } from "@marketplace/shared/utils";

const OTP_SECRET_KEY = "8785FCRqGXijn5ygs1I60FAX8slfmsJPz3iqf6/4+n4=";

/**
 * Interface for OTP data
 */
export interface OtpData {
  email: string;
  expirationTime: number;
}

/**
 * A singleton service for handling OTP (One-Time Password) encryption and decryption.
 * Uses AES encryption to secure OTP data.
 *
 * @example
 * ```typescript
 * // Get OTP data
 * const otpData = await OtpService.getInstance().encrypt('user@example.com', Date.now() + 300000);
 *
 * // Verify OTP data
 * const { email, expirationTime } = await OtpService.getInstance().decrypt(otpData);
 * ```
 */
export class OtpService {
  private static instance: OtpService;
  private readonly encryptionService: EncryptionService;

  private constructor() {
    this.encryptionService = new EncryptionService({
      secretKey: OTP_SECRET_KEY,
      onError: captureException
    });
  }

  /**
   * Gets the singleton instance of OtpService
   * @returns The OtpService instance
   */
  public static getInstance(): OtpService {
    if (!OtpService.instance) {
      OtpService.instance = new OtpService();
    }
    return OtpService.instance;
  }

  /**
   * Encrypts OTP data
   * @param email The email address
   * @param expirationTime The expiration timestamp in milliseconds
   * @returns Encrypted OTP data as a string
   * @throws Error if encryption fails
   */
  public async encrypt(email: string, expirationTime: number): Promise<string> {
    try {
      const otpData: OtpData = {
        email,
        expirationTime
      };
      return await this.encryptionService.encrypt(otpData);
    } catch (error) {
      console.error(error);
      captureException(new Error(`Failed to encrypt OTP data: ${error}`));
      throw new Error("Failed to encrypt OTP data", { cause: error });
    }
  }

  /**
   * Decrypts OTP data
   * @param encryptedData The encrypted OTP data
   * @returns Decrypted OTP data containing email and expiration time
   * @throws Error if decryption fails or data is invalid
   */
  public async decrypt(encryptedData: string): Promise<OtpData> {
    try {
      const decryptedData =
        await this.encryptionService.decrypt<OtpData>(encryptedData);

      // Validate decrypted data
      if (!decryptedData.email || !decryptedData.expirationTime) {
        throw new Error("Invalid OTP data");
      }

      return decryptedData;
    } catch (error) {
      console.error(error);
      captureException(new Error(`Failed to decrypt OTP data: ${error}`));
      throw new Error("Failed to decrypt OTP data", { cause: error });
    }
  }

  /**
   * Checks if OTP data is expired
   * @param expirationTime The expiration timestamp to check
   * @returns true if expired, false otherwise
   */
  public isExpired(expirationTime: number): boolean {
    return Date.now() > expirationTime;
  }
}
